import { useQuery } from '@apollo/client';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { ActionRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Action_Bool_Exp,
  GetActionsQuery,
  Issue_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetActionsDocument,
  GetIssuesDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcActionsToGraphQL } from 'src/hooks/queries/action/useGetActionsRegister';
import { useLabelledFields as useActionLabelledFields } from 'src/pages/actions/useLabelledFields';
import { useLabelledFields as useIssueLabelledFields } from 'src/pages/issues/useLabelledFields';
import type { LabelledIdArray } from 'src/rbac/types';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import { UNRATED } from '../../gigawidget/types';
import { dateRangeFilter } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dateRangeClickthroughFilter,
  defaultClickthroughFilter,
} from '../dataSourceHelpers';
import {
  getActionOverdueLabel,
  ownersField,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

export type IssueActionRow = TableRecord & {
  Id: string;
  IssueId: string;
  IssueFriendlyId: string;
  IssueTitle: string;
  IssueType: string;
  IssueSeverity: string;
  // Issue status includes the overdue fold (matches single-entity issue source).
  IssueStatus: string;
  ActionId: string;
  ActionFriendlyId: string;
  ActionTitle: string;
  // Labelled status excluding the overdue fold — overdue is its own column/getter.
  ActionStatus: string;
  ActionPriority: string;
  ActionDateDue: string;
  ActionOwners: LabelledIdArray;
  ActionOverdue: string;
};

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Issue, Parent_Type_Enum.Action],
  documentNode: GetActionsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.action.register.queryOptions({});
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: ActionRegisterResponse) => mapTrpcActionsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Action_Bool_Exp>(Parent_Type_Enum.Action),
  }),
  useTablePropsHook: (
    data: GetActionsQuery | undefined,
    options: StatefulTableOptions<IssueActionRow>
  ) => {
    const fields = useMemo<TableFields<IssueActionRow>>(
      () => ({
        IssueFriendlyId: simpleField(i18n.t('columns.id')),
        IssueTitle: simpleField(i18n.t('issues.columns.title')),
        IssueType: simpleField(i18n.t('issues.columns.type')),
        IssueSeverity: simpleField(i18n.t('issues.columns.severity')),
        IssueStatus: simpleField(i18n.t('issues.columns.status')),
        ActionFriendlyId: simpleField(i18n.t('columns.id')),
        ActionTitle: simpleField(i18n.t('actions.columns.title')),
        ActionStatus: simpleField(i18n.t('actions.columns.status')),
        ActionPriority: simpleField(i18n.t('actions.columns.priority')),
        ActionDateDue: dateColumnFromConfig<IssueActionRow>({
          header: { header: i18n.t('actions.columns.due_date') },
          dateField: 'ActionDateDue',
        }),
        ActionOwners: ownersField(
          i18n.t('actions.columns.owner'),
          (item) => item.ActionOwners
        ),
        ActionOverdue: simpleField(i18n.t('actions.columns.overdue')),
      }),
      []
    );

    const actionsLabelled = useActionLabelledFields(data?.action);
    const { getLabel: getActionStatusLabel } = useRating('action_status');

    const issueWhere = useEntityWhereFilter<Issue_Bool_Exp>(
      Parent_Type_Enum.Issue
    );
    const { data: issueData } = useQuery(GetIssuesDocument, {
      variables: { where: issueWhere },
    });
    // Relationship widget spans all issue sub-types; label against the base Issue taxonomy.
    const issuesLabelled = useIssueLabelledFields(
      Parent_Type_Enum.Issue,
      issueData?.issue
    );

    const joinedData = useMemo<IssueActionRow[] | undefined>(() => {
      if (!actionsLabelled || !issuesLabelled) {
        return undefined;
      }

      const issueMap = new Map(issuesLabelled.map((i) => [i.Id, i]));

      return actionsLabelled.flatMap((action) =>
        action.parents
          .filter(
            (p) =>
              p.parent?.ObjectType === Parent_Type_Enum.Issue && p.parent.Id
          )
          .map((parent) => {
            const issue = issueMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${action.Id}`,
              IssueId: parent.parent!.Id,
              IssueFriendlyId: issue?.SequentialIdLabel ?? '',
              IssueTitle: issue?.Title ?? parent.issue?.Title ?? '',
              IssueType: issue?.IssueTypeLabelled ?? '',
              IssueSeverity: issue?.SeverityLabelled ?? '',
              IssueStatus: issue?.StatusLabelled ?? '',
              ActionId: action.Id,
              ActionFriendlyId: action.SequentialIdLabel ?? '',
              ActionTitle: action.Title ?? '',
              ActionStatus: action.Status
                ? (getActionStatusLabel(action.Status) ?? '')
                : '',
              ActionPriority: action.PriorityLabelled ?? '',
              ActionDateDue: action.DateDue ?? '',
              ActionOwners: action.allOwners,
              ActionOverdue: getActionOverdueLabel(action),
            };
          })
      );
    }, [actionsLabelled, issuesLabelled, getActionStatusLabel]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('issue_action_other'),
      options
    );
  },
  entityNamePlural: 'issue_action_other',
  entityNameSingular: 'issue_action_one',
  fields: 'issueActions.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'issueFriendlyId',
      name: () => i18n.t('columns.id'),
      categoryGetter: (item: IssueActionRow) => item.IssueFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('IssueFriendlyId'),
    },
    {
      id: 'issueTitle',
      name: () => i18n.t('issues.columns.title'),
      categoryGetter: (item: IssueActionRow) => item.IssueTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('IssueTitle'),
    },
    {
      id: 'issueType',
      name: () => i18n.t('issues.columns.type'),
      categoryGetter: (item: IssueActionRow) => item.IssueType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('IssueType'),
    },
    {
      id: 'issueSeverity',
      name: () => i18n.t('issues.columns.severity'),
      categoryGetter: (item: IssueActionRow) => item.IssueSeverity || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('IssueSeverity'),
      ratingColourKey: 'severity',
    },
    {
      id: 'issueStatus',
      name: () => i18n.t('issues.columns.status'),
      categoryGetter: (item: IssueActionRow) => item.IssueStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('IssueStatus'),
      ratingColourKey: 'issue_assessment_status',
    },
    {
      id: 'actionFriendlyId',
      name: () => i18n.t('columns.id'),
      categoryGetter: (item: IssueActionRow) =>
        item.ActionFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionFriendlyId'),
    },
    {
      id: 'actionTitle',
      name: () => i18n.t('actions.columns.title'),
      categoryGetter: (item: IssueActionRow) => item.ActionTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionTitle'),
    },
    {
      id: 'actionStatus',
      name: () => i18n.t('actions.columns.status'),
      categoryGetter: (item: IssueActionRow) => item.ActionStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionStatus'),
      ratingColourKey: 'action_status',
    },
    {
      id: 'actionPriority',
      name: () => i18n.t('actions.columns.priority'),
      categoryGetter: (item: IssueActionRow) => item.ActionPriority || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionPriority'),
      ratingColourKey: 'priority',
    },
    {
      id: 'actionDateDue',
      name: () => i18n.t('actions.columns.due_date'),
      categoryGetter: (item: IssueActionRow) =>
        item.ActionDateDue ? new Date(item.ActionDateDue) : new Date(0),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        where: { DateDue: dateRangeFilter(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('ActionDateDue'),
    },
    {
      id: 'actionOwner',
      name: () => i18n.t('actions.columns.owner'),
      categoryGetter: (item: IssueActionRow) =>
        item.ActionOwners.map((owner) => ({
          key: owner.id,
          label: owner.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'ActionOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'actionOverdue',
      name: () => i18n.t('actions.columns.overdue'),
      categoryGetter: (item: IssueActionRow) => item.ActionOverdue,
      clickthroughFilter: defaultClickthroughFilter('ActionOverdue'),
    },
  ],
});
